'use strict';

import {
    Buildings,
    isDesktop, isIOS,
    isMobile,
    isTablet, resourcesFolder,
    settingsData,
    threeJsVariables,
} from '../../settings';
import {
    Mesh,
    ShaderMaterial,
    Vector2,
    TOUCH,
    Group,
    Color,
    Vector3,
    OrthographicCamera,
    MOUSE,
    MeshBasicMaterial,
    TextureLoader,
    EdgesGeometry,
    LineSegments,
    LineBasicMaterial,
    Box3,
    PlaneGeometry,
    Object3D,
    UniformsUtils, LinearSRGBColorSpace, Quaternion, Euler, RepeatWrapping,
} from 'three';
import liteModeVertex from '../threejs/Shaders/liteMode/liteModeVertex.glsl';
import liteModeFragment from '../threejs/Shaders/liteMode/liteModeFragment_2.glsl';
import * as dat from 'dat.gui';
import { compassVisibleToggle } from '../components/compass/compass';
import {
    createOrbitToMapButton,
    hideOrbitToMapButton,
    showOrbitToMapButton
} from '../controls/orbitControls';
import gsap from 'gsap';
import {
    OrbitToMap
} from '../threejs/camera';
import {
    checkHighlightsAndCrmDataAreInPlace,
    concatPropsAndHighlights,
    createBuildingInfo, setAttributeToPlug, setDataToAmenities,
    setDataToApartment, setDataToPlug, setHardcodeDataToApartment, setHighlights,
} from '../threejs/autoSelectionBoxes';
import {
    addAmenitiesWithoutEngineId, checkHash, checkImageExists,
    createMaterialAndSetBaseColor,
    getUrlParam, isPortraitMode,
} from '../../functions';
import { Building, BuildingWithoutModel } from '../building/Building';
import { ViewMode } from '../../common';
import {OrbitControls} from '../../libs/OrbitControls';
import { Sea } from '../liveAnimations/litemode/sea/Sea';
import { Cars } from '../liveAnimations/litemode/cars/Cars';
import { Sky } from '../liveAnimations/litemode/sky/Sky';
import { Windows } from '../liveAnimations/litemode/windows/Windows';
import { Trees } from '../liveAnimations/litemode/trees/Trees';
import { flatLabelPictureLiteMode } from '../components/flatLabels/flatLabels';
import { buildingLabelLiteMode } from '../components/building-switch/buildingLabels';
import { Plug } from '../liveAnimations/litemode/plug/Plug';
import { Loader } from '../threejs/loader/Loader';
import { Pass } from 'three/examples/jsm/postprocessing/Pass';
import { createTooltip } from '../components/flatTooltip/flatTooltip';
import { BaseElement as Button, BaseElement } from '../interfaces/BaseElement';
import { Cloudflare } from '../api/Cloudflare';
import { CSS2DObject } from '../../libs/CSS2DRenderer';
import { getCurrentLang } from '../components/langSwitcher/langSwitcher';
import { PhotoSwiperGallery } from '../menu/gallery/galleryDesktop';
import { DreamsPreloader } from '../components/preloader/DreamsPreloader';
import { BmbyMode } from './BmbyMode';
import { SchematicBuilding } from '../threejs/SchematicBuilding/SchematicBuilding';
import { getBuildingShape, getGalleryData, getMarketingProps, setBuildingShape } from '../crm/crm-requests';
import { Rotor } from './Rotor';
import {CylinderBase} from '../cylinderGrid/CylinderBase';
import { createAnimatedProjectLabel } from '../widgets/poi/poiv3';
import { enterToTheBuildingWithMaxAvailableApartments } from '../components/building-switch/building_switch';
import { Neighborhood } from '../liveAnimations/litemode/neighborhood/Neighborhood';

export class LiteMode extends Pass{
    #renderSizeVec = new Vector2();
    #pointDown = new Vector2();
    #pointUp = new Vector2();
    #maxPan = new Vector3();
    #minPan = new Vector3();
    #vMin = new Vector3();
    #vMax = new Vector3();
    #box = new Box3();
    #zoomTargetSize = new Vector3();
    #zoomTargetCenter = new Vector3();
    #bounceZoom = 0;
    #tryCount = 0;
    #increaseBtn = null;
    #decreaseBtn = null;

    constructor () {
        super();
        this.active = config.features.liteMode?.active || false;
        this.material = null;
        this.overlay = null;
        this.imageWidth = 0;
        this.imageHeight = 0;
        this.aspect = new Vector2(1.0, 1.0);
        this.camera =  new OrthographicCamera(-1, 1, 1, -1, 0, 1);
        // this.raycaster = new Raycaster();
        this.intersections = [];
        this.intersectedObject = null;
        this.controls = new OrbitControls(this.camera,threeJsVariables.renderer.domElement);
        this.models = [];
        this.blockPointerEvents = false;
        this.settings = {};
        this.currentZoom = 1.0;
        this.prevZoom = 1;
        this.canvas = document.querySelector('#c');
        this.blockEvents = false;
        this.debugActive = getUrlParam('lite-mode-debug');
        this.resourcesUrl = `${config.cdnRepository}/${config.projectFolder}/${resourcesFolder}/litemod/`;
        this.mode = 'picture';
        this.textureLoader = new TextureLoader();
        this.debug = config.features.liteMode?.debug || false;
        this.zoomTarget = null;
        this.pictureModeOrbitIntersections = [];
        this.debug = config.features.liteMode?.debug || false;
        this.debugFolder = null;
        this.depthUpdate = true;
        this.flatLabelsForPictureMode = [];

        switch (config.features.liteMode?.mode) {
            case 'regular':
                this.mode = 'regular';
                break;
            case 'schematic':
                this.mode = 'schematic';
                break;
            case 'empty':
                this.mode = 'empty';
                break;
            case 'rotate':
                this.mode = 'rotate';
                break;
        }

        if (isMobile || isTablet) {
            this.imageUrl = `${config.cdnRepository}/resize/image/width=2000,quality=70/${config.projectFolder}/${resourcesFolder}/litemod/`;
        } else if (isDesktop) {
            this.imageUrl = `${config.cdnRepository}/resize/image/width=4000,quality=95/${config.projectFolder}/${resourcesFolder}/litemod/`;
        } else {
            this.imageUrl = `${config.cdnRepository}/resize/image/width=4000,quality=70/${config.projectFolder}/${resourcesFolder}/litemod/`;
        }
    }

    async init (loader) {
        if (!this.isActive()) return;

        const { images, settings, mapping,models } = config.features.liteMode;
        this.settings = settings;
        this.models = models;
        this.amenities = config.features.liteMode.amenities || [];

        this.controls.screenSpacePanning = true;
        this.controls.minZoom = 1;
        this.controls.maxZoom = settings.zoom.max;
        this.controls.mouseButtons = {
            LEFT: MOUSE.PAN,
            MIDDLE: MOUSE.DOLLY
        };

        this.controls.touches = {
            ONE: TOUCH.PAN,
            TWO: TOUCH.DOLLY_PAN
        };

        this.controls.enableDamping = isMobile ? false : settings.damping.active;
        this.controls.dampingFactor = settings.damping.dampingFactor;
        this.controls.zoomSpeed = settings.zoom.step;

        this.controls.maxPan = this.#maxPan;
        this.controls.minPan = this.#minPan;
        this.controls.object.zoom = settings.zoom.initial;
        this.controls.object.updateProjectionMatrix();

        this.controls.addEventListener('change', this.#controlsHandler.bind(this));
        
        if (config.features.liteMode.enterOnClick) {
            threeJsVariables.renderer.domElement.addEventListener('pointerdown',this.clickHandler.bind(this));
            threeJsVariables.renderer.domElement.addEventListener('pointerup',this.clickHandler.bind(this));
        }

        if (this.debug) {
            const gui = new dat.GUI();
            this.debugFolder = gui.addFolder('LiteMode');
        }

        // Load selections
        const textureLoader = loader.textureLoader;
        const fbxLoader = loader.fbxLoader;
        const versionedMapping = `${mapping}?v=${Cloudflare.version}`;
        if (!this.modeIs('empty') && !this.modeIs('rotate')) {
            fbxLoader.load(this.resourcesUrl + versionedMapping, selections => {
                selections.traverse(el => {
                    if (el.isMesh) {

                        const newMaterial = new MeshBasicMaterial({
                            depthTest: false,
                            depthWrite: false,
                            transparent: true,
                            opacity: 0
                        });

                        const geom = el.geometry.clone();
                        const mesh = new Mesh(geom,newMaterial);
                        mesh.name = el.name;
                        mesh.scale.copy(el.scale);
                        mesh.position.set(el.position.x,el.position.y,-0.2);

                        sceneObjects.add(mesh);

                        if (this.mode !== 'picture'){
                            mesh.userData.type = 'building';
                        }

                        this.intersections.push(mesh);
                        settingsData.objects_to_intersection.push(mesh);
                    }
                });
            });
        }

        const sceneObjects = new Group();
        this.group = sceneObjects;

        let depthImage = null;
        let nightTexture = null;
        let generalImage = null;
        
        let url = `${this.imageUrl}${images.general}?v=${Cloudflare.version}`;
        if (this.modeIs('empty')) {
            const response = await getGalleryData();
            const mainImage = response.data && response.data
                .flatMap(dataItem => dataItem?.pictures || [])
                .find(picture => picture?.mainImage);
        
            if (mainImage) {
                url = mainImage.imageUrl;
            }
        }
        
        if (!this.modeIs('rotate')) {
            generalImage = textureLoader.load(url, loadedImage => {
                loadedImage.colorSpace = LinearSRGBColorSpace;
                threeJsVariables.renderer.initTexture(loadedImage);
                this.imageWidth = loadedImage.image.width;
                this.imageHeight = loadedImage.image.height;
        
                this.updateAspect();
            });
        }

        this.depthSettings = {
            depthTexture: { value: null },
            depthFocus: { value: 0 },
            depthStrength: { value:0 },
            uDepthMouse: { value: new Vector2() },
            gyroscope: 1
        };

        if (typeof images.depth === 'object' && images.depth.image && !this.modeIs('rotate')){

            if ((isMobile || isTablet)) {
                if (!DeviceMotionEvent) {
                    console.warn("This browser does not support requesting DeviceMotionEvent permission.");
                    return;
                }
                window.gsens = -2.5;
                this.gimbal = new Gimbal();

                if (!isIOS) {
                    window.addEventListener('deviceorientation', () => {
                        this.gimbal.update();
                        threeJsVariables.gyroscope.x = Math.min(Math.max( this.gimbal.yaw * this.depthSettings.gyroscope,-1),1);
                        threeJsVariables.gyroscope.y = Math.min(Math.max( this.gimbal.pitch * this.depthSettings.gyroscope,-1),1);

                        this.updateDepthPosition(threeJsVariables.gyroscope,'gyroscope');
                    }, false);

                    this.gimbal.enable();
                    this.gimbal.recalibrate();
                }
            }

            this.depthSettings.depthTexture.value = textureLoader.load(this.imageUrl + images.depth.image,loadedTexture => {
                loadedTexture.colorSpace = LinearSRGBColorSpace;
            });
            this.depthSettings.depthFocus.value = images.depth.focus || 0;
            this.depthSettings.depthStrength.value = images.depth.strength || 0;

            if (this.debug) {
                const depthFolder = this.debugFolder.addFolder('Depth');
                depthFolder.add(this.depthSettings.depthFocus,'value',-1,0,0.001).name('Focus').onChange(v => this.material.uniforms.depthFocus.value = v);
                depthFolder.add(this.depthSettings.depthStrength,'value',0,5,0.001).name('Strength').onChange(v => this.material.uniforms.depthStrength.value = v);
                depthFolder.add(this.depthSettings,'gyroscope',-5,5,0.1);
            }
        }

        if (images.night && !this.modeIs('rotate')) {
            nightTexture = textureLoader.load(`${this.imageUrl}${images.night}?v=${Cloudflare.version}`);
            nightTexture.colorSpace = LinearSRGBColorSpace;
        }

        this.sky = new Sky(this.resourcesUrl,fbxLoader);
        this.sky.create().then(model => {
            if (typeof model === 'string') return;
            sceneObjects.add(model);
            threeJsVariables.renderList.reorder();
        });

        this.sea = new Sea(this.resourcesUrl,generalImage,nightTexture,fbxLoader,depthImage);
        this.sea.create().then((mesh)=>{
            if (typeof mesh === 'string') return;
            sceneObjects.add(mesh);
            threeJsVariables.renderList.reorder();
        });

        this.cars = new Cars(this.resourcesUrl,fbxLoader);
        this.cars.create().then(model => {
            if (typeof model === 'string') return;
            sceneObjects.add(model);
            threeJsVariables.renderList.reorder();
        });

        this.plug = new Plug(this.resourcesUrl,generalImage,nightTexture,fbxLoader);
        this.plug.create().then((mesh) => {
            if (typeof mesh === 'string') return;
            sceneObjects.add(mesh);
            threeJsVariables.renderList.reorder();
        });

        this.windows = new Windows(this.resourcesUrl,fbxLoader);
        this.windows.create().then(model => {
            if (typeof model === 'string') return;
            sceneObjects.add(model);
            threeJsVariables.renderList.reorder();
        });

        this.trees = new Trees(this.resourcesUrl,generalImage,nightTexture,fbxLoader,depthImage);
        this.trees.create().then(model => {
            if (typeof model === 'string') return;
            sceneObjects.add(model);
            threeJsVariables.renderList.reorder();
        });
        
        this.neighborhood = new Neighborhood(this.resourcesUrl,fbxLoader)
        this.neighborhood.create().then(group => {
            if (typeof group === 'string') return;
            sceneObjects.add(group);
            this.intersections.push(...group.children);
            settingsData.objects_to_intersection.push(...group.children);
            threeJsVariables.renderList.reorder();
        });

        const geometry = new PlaneGeometry(2, 2, 1, 1);

        this.material = new ShaderMaterial({
            vertexShader: liteModeVertex,
            transparent: true,
            fragmentShader: liteModeFragment,
            uniforms: {
                generalTexture: {
                    value: generalImage,
                },
                readBufferTexture: {
                    value: null
                },
                readBufferSize: {
                    value: new Vector2()
                },
                generalNightTexture: {
                    value: nightTexture
                },
                orbitToMapProgress: {
                    value: 0.0
                },
                uCircleScale: {
                    value: 0.0
                },
                uSubstrateOpacity: {
                    value: 0.0
                },
                progress: {
                    value: 0.0
                },
                uTime: {
                    value: 0.0,
                },
                uTimesOfDay: {
                    value: 0.0
                }
            },
        });

        this.material.uniforms = UniformsUtils.merge([this.material.uniforms,this.depthSettings]);

        this.overlay = new Mesh(geometry, this.material);
        this.overlay.scale.set(1 / this.aspect.x, 1 / this.aspect.y, 1);
        this.overlay.name = 'Lite mode';
        this.overlay.add(sceneObjects);

        threeJsVariables.scene.add(this.overlay);
        threeJsVariables.renderList.reorder();

        this.#controlsHandler(undefined,true);

        this.controls.target.set(this.settings.zoom.position.x,this.settings.zoom.position.y,0);
        this.camera.position.set(this.settings.zoom.position.x,this.settings.zoom.position.y,0);
        this.createAmenities();
        this.createOneLabel();
    
        if (this.modeIs('rotate')) {
            this.controls.mouseButtons.LEFT = null;
            this.controls.touches.ONE = null;
            this.controls.mouseButtons.RIGHT = MOUSE.PAN;
            this.controls.maxZoom /= 2;
            this.rotor = new Rotor(this,loader);
        }
    }
    
    clickHandler(e) {
        if (!this.isActive() || !this.isVisible()) return;
        
        switch (e.type) {
            case 'pointerdown':
                this.#pointDown.set(Number(e.pageX.toFixed(3)),Number(e.pageY.toFixed(3)));
                break;
            case 'pointerup':
                this.#pointUp.set(Number(e.pageX.toFixed(3)),Number(e.pageY.toFixed(3)));
                if (this.#pointDown.equals(this.#pointUp)) {
                    enterToTheBuildingWithMaxAvailableApartments();
                }
                break;
        }
    }

    iosGyroscopePermission() {
        if (isMobile && isIOS && this.isActive() && this.modeIs('empty') && this.gimbal) {
            DeviceMotionEvent.requestPermission && DeviceOrientationEvent.requestPermission().then(response => {
                if (response === 'granted') {
                    window.addEventListener('deviceorientation', () => {
                        this.gimbal.update();
                        threeJsVariables.gyroscope.x = Math.min(Math.max( this.gimbal.yaw,-1),1);
                        threeJsVariables.gyroscope.y = Math.min(Math.max( this.gimbal.pitch,-1),1);
                        this.updateDepthPosition(threeJsVariables.gyroscope,'gyroscope');
                    }, false);
                    this.gimbal.enable();
                    this.gimbal.recalibrate();
                }
            });
        }
    }

    #controlsHandler(e,manualUpdate = false) {
        if (!this.isActive() || !this.isVisible() || this.currentZoom === this.camera.zoom && !manualUpdate) return;
        this.currentZoom = this.camera.zoom;

        const planeSizeX = 2 * this.overlay.scale.x;
        const planeSizeY = 2 * this.overlay.scale.y;
        const x = (planeSizeX * this.currentZoom - 2) / 2 / this.currentZoom;
        const y = (planeSizeY * this.currentZoom - 2) / 2 / this.currentZoom;

        this.#vMin.set(-x,-y,0);
        this.#vMax.set(x,y,0);

        this.controls.target.clamp(this.#vMin,this.#vMax);
        this.controls.object.position.clamp(this.#vMin,this.#vMax);

        this.#maxPan.set(x,y,0);
        this.#minPan.set(-x,-y ,0);

        this.zoomListener();
        this.prevZoom = this.currentZoom;
        this.resizeFlatLabels();
    }
    
    createOneLabel() {
        if (config.features.liteMode.oneLabel) {
            
            let labelsGroup = this.group.getObjectByName('Labels');
            if (!labelsGroup) {
                labelsGroup = new Group();
                labelsGroup.name = 'Labels';
                this.group.add(labelsGroup);
            }
            
            const {x,y, scale} = config.features.liteMode.oneLabel;
            createAnimatedProjectLabel(true)
                .then(svg => {
                    const labelContainer = new BaseElement()
                        .addClass('building-labels','active','all-in-one__label')
                        .event('click',() => enterToTheBuildingWithMaxAvailableApartments())
                        .DOMElement;
    
                    labelContainer.innerHTML = `
                    <div class="building-labels_wrapper all-in-one__label-wrapper building-labels__private-house show-click-here" data-name="privateHouse" data-label="labelTower-all-in-one">
                        <div class="building-label-bubble">${svg}</div>
                         <div class="building-labels__icon" style="background: url(${Cloudflare.resize(config.general.logo, {width: 200})})"></div>
                    `;
                    
                    const label = new CSS2DObject(labelContainer);
                    label.position.set(x,y,0);
                    label.userData.zoom = scale || 1;
                    labelsGroup.add(label);
                })
        }
    }

    createAmenities() {
        if (!this.amenities.length) return;

        this.amenitiesGroup = new Group();
        this.amenitiesGroup.name = 'Amenities';
        this.overlay.add(this.amenitiesGroup);

        this.amenities.forEach(amenity => {
            const label = new LiteModeAmenities(amenity);
            label.position.set(amenity.position.x,amenity.position.y,0);
            this.amenitiesGroup.add(label);
        });
    }

    amenitiesToggle(state) {
        if (!this.amenitiesGroup) return;

        this.amenitiesGroup.traverse(el => {
            if (el.type === 'Object3D') {
                if (state === 'show') {
                    el.element.classList.remove('hidden');
                } else {
                    el.element.classList.add('hidden');
                }
            }
        });
    }

    buildingLabelsToggle(state) {
        const labels = this.group.getObjectByName('Labels');
        if (labels) {
            labels.traverse(el => {
                if (el.type === 'Object3D') {
                    if (state === 'show') {
                        el.element.classList.remove('hidden');
                    } else {
                        el.element.classList.add('hidden');
                    }
                }
            });
        }
    }

    resizeFlatLabels() {
        if (!this.modeIs('picture') || !this.flatLabelsForPictureMode.length) return;

        if (isMobile) {
            let zoom = 1;
            if (isPortraitMode()) {
                zoom = Math.min(Math.max(this.camera.zoom - 1, 0.4), 1);
            } else {
                zoom = 1 - 1 / this.camera.zoom;
            }
            this.flatLabelsForPictureMode.forEach(label => {
                label.userData.zoom = this.camera.zoom > 1.5 ? zoom : 0;
            });
        } else {
            this.flatLabelsForPictureMode.forEach(label => {
                label.userData.zoom = Math.min(Math.max(this.camera.zoom - 1, 0.6), 1);
            });
        }
    }

    async loadBuildingModel() {
        if (this.mode === 'picture'){
            const edgesColor = new Color().setStyle(config.features.liteMode.settings.edges?.color || '#000000',LinearSRGBColorSpace);
            const allBuildings = config.crmProjectSettings.buildings;

            allBuildings.forEach(building => {
               const buildingObject = new BuildingWithoutModel(building.buildingEngineId,settingsData.allCrmData,{data: config.crmProjectSettings});
               buildingObject.apartments = settingsData.objects_to_intersection.filter(mesh => mesh.name.startsWith(building.buildingEngineId));

               buildingObject.apartments.forEach(apt => {
                   const existProp = settingsData.allCrmData.find(prop => prop.engineId === apt.name);
                   if (existProp) {
                       setDataToApartment(apt, existProp);

                       const edges = new EdgesGeometry(apt.geometry);
                       const line = new LineSegments(edges, new LineBasicMaterial({
                           color: edgesColor ,
                           transparent: true,
                           opacity: 0,
                           depthTest: false
                       }));
                       line.name = 'edges';

                       apt.add(line);
                   }
               });

               settingsData.objects_to_intersection = settingsData.objects_to_intersection.filter(mesh => !mesh.name.startsWith(building.buildingEngineId));
               settingsData.objects_to_intersection.forEach(obj => obj.userData.type = 'building');

               Buildings.addBuilding(building.buildingEngineId,buildingObject);
            });

            this.zoomTarget = settingsData.objects_to_intersection.find(el => el.name === config.resources.bundle.building.name) || null;
            this.pictureModeOrbitIntersections = settingsData.objects_to_intersection;
            if (!isMobile) createTooltip();

        } else if (this.mode === 'schematic'){
            const projectModels = config.features.liteMode.models;
            const loader = new Loader();
            
            function prepareSelections (selBox,currentBuilding) {
                selBox.traverse((element) => {
                    if (element.isMesh) {

                        const currentFloor = parseInt(element.parent.name.split('_')[1]);

                        if (!element.name.startsWith(config.resources.bundle.selectionBoxes.floorPlugName)){

                            const existProp = settingsData.allCrmData.find(prop => prop.engineId === element.name);
                            if (existProp) {
                                setDataToApartment(element, existProp);
                                createMaterialAndSetBaseColor(element);
                                currentBuilding.apartments.push(element);
                            } else {
                                setHardcodeDataToApartment(element, currentFloor);
                                createMaterialAndSetBaseColor(element);
                            }

                        } else {
                            setAttributeToPlug(element, currentFloor);
                            setDataToPlug(element, currentFloor);
                            currentBuilding.floorsPlug.push({floor: currentFloor, data: element});
                        }

                        if (element.name.match(/_amenity_/g)) {
                            createMaterialAndSetBaseColor(element);
                            setDataToAmenities(element);
                            if (element.userData.crm_data)
                                currentBuilding.customSelections.push(element);
                        }
                    }
                });
    
                selBox.children.forEach((child) => {
                    const currentFloor = parseInt(child.name.split('_')[1]);
                    child.userData.basePosition = new Vector3(child.position.x, child.position.y, child.position.z);
                    currentBuilding.selections.push({floor: currentFloor, data: child.children});
                    currentBuilding.floorSliderNumbers.push(currentFloor);
                });
    
                currentBuilding.floorSliderNumbers.sort((a,b) => b - a);
            }
            
            if (projectModels.length === 0) {
                config.threejs.compass.correction = 0;
                threeJsVariables.slicer.params.distance = 500000;
                threeJsVariables.slicer.setDefaultPlanes();
                threeJsVariables.sceneObjectsSlicer.params.distance = 500000;
                threeJsVariables.sceneObjectsSlicer.setDefaultPlanes();
                CylinderBase.updateRotation();
                
                threeJsVariables.schematicBuilding = new SchematicBuilding(settingsData.allCrmData);
                const type = config.features.liteMode.buildingType;
                
                if (type) {
                    if (Array.isArray(type)) {
                        threeJsVariables.schematicBuilding.type = 'custom';
                        threeJsVariables.schematicBuilding.setShape(type);
                    } else {
                        threeJsVariables.schematicBuilding.type = type;
                    }
                } else {
                    const response = await getBuildingShape();
                    if (response && response.data) {
                        threeJsVariables.schematicBuilding.type = response.data.type;
                        if (threeJsVariables.schematicBuilding.type === 'custom') {
                            threeJsVariables.schematicBuilding.setShape(response.data.shape);
                        }
                        
                        if (response.data.advanced) {
                            threeJsVariables.schematicBuilding.advancedSettings = response.data.advanced;
                        }
                    }
                }
    
                if (checkHash('admin')) {
                    threeJsVariables.schematicBuilding.createUI();
                }

                threeJsVariables.schematicBuilding.create();
                const selections = [];
                
                Object.entries(threeJsVariables.schematicBuilding.buildings).forEach(([name,data]) => {
                    selections.push(data.selectionsGroup);
                    const model = data.buildingGroup.children[0];
                    model.layers.set(0);
                    const building = new Building(model);
                    const modelGroup = building.create();
                    data.buildingGroup.removeFromParent();
                    threeJsVariables.schematicBuilding.buildingGroup.add(modelGroup);
                    building.amenities.Roof = new Object3D();
                    threeJsVariables.slicer.add(model);
                });
                
                concatPropsAndHighlights({data:settingsData.allCrmData},selections,{data: config.crmProjectSettings});
                threeJsVariables.scene.add(threeJsVariables.schematicBuilding.buildingGroup);

                setHighlights(threeJsVariables.schematicBuilding.selectionsGroup);

                buildingLabelLiteMode();
                if (!isMobile) createTooltip();
                return;
            }
            
            const fbxLoader = loader.fbxLoader;
            const selectionBoxesModels = {};
            const selectionBoxesCrmData = {};
            const projectGroup = new Group();
            projectGroup.name = 'Project Selection Boxes';

            for (let i = 0; i < projectModels.length; i++) {
                const url = this.resourcesUrl + projectModels[i].building + '_selectBoxes.fbx';
                fbxLoader.load(url,loadedModel => {
                    projectGroup.add(loadedModel);
                    loadedModel.name = loadedModel.name.split('_')[0];
                    selectionBoxesModels[loadedModel.name.split('_')[0]] = loadedModel;
                });
            }

            loader.addCallback = () => {
                for (let key in selectionBoxesModels) {
                    // const floorsPlug = [];
                    new Building(selectionBoxesModels[key]).create();
                    const currentBuilding = Buildings.getBuilding(key);
    
                    prepareSelections(selectionBoxesModels[key],currentBuilding);

                    addAmenitiesWithoutEngineId(currentBuilding);
                    
                    currentBuilding.selectionsGroup = selectionBoxesModels[key];
                    currentBuilding.model = null;
                    currentBuilding.instances = null;
                    currentBuilding.info = createBuildingInfo(selectionBoxesModels[key], settingsData.allCrmData, {data: config.crmProjectSettings});
                }

                setHighlights(projectGroup);
                buildingLabelLiteMode();
                if (!isMobile) createTooltip();
            };
        } else if (this.mode === 'empty') {
            const allBuildings = config.crmProjectSettings.buildings;

            allBuildings.forEach(building => {
                const buildingObject = new BuildingWithoutModel(building.buildingEngineId, settingsData.allCrmData, {data: config.crmProjectSettings});
                buildingObject.apartments = buildingObject.info.crm_data.map((el,index) => {
                    const obj = new Object3D();
                    obj.name = el.engineId;
                    obj.material = new MeshBasicMaterial();
                    setDataToApartment(obj,el,index);
                    return obj;
                });
                Buildings.addBuilding(building.buildingEngineId,buildingObject);
            });
            buildingLabelLiteMode();
        }

        checkHighlightsAndCrmDataAreInPlace(settingsData.allCrmData);
    }

    activeViewMode() {
        if (!this.isActive()) return;

        switch (this.mode) {
            case 'picture':
            case 'empty':

                switch (settingsData.activeViewMode) {
                    case ViewMode.Cylinder:

                        this.#pictureCylinderLogic();

                        break;
                    case ViewMode.Orbit:

                        this.#pictureOrbitLogic();

                        break;

                }

                break;
            case 'schematic':

                break;
        }
    }

    updateDepthPosition(vec2 = threeJsVariables.mouse,type = 'pointer') {
        if (this.material.uniforms.depthTexture.value && this.depthUpdate) {
            if (isMobile || isTablet) {
                if (type === 'gyroscope') {
                    this.material.uniforms.uDepthMouse.value.copy(vec2);
                    this.sea.updateDepthPosition(vec2);
                    this.trees.updateDepthPosition(vec2);
                }
            } else {
                this.material.uniforms.uDepthMouse.value.copy(vec2);
                this.sea.updateDepthPosition(vec2);
                this.trees.updateDepthPosition(vec2);
            }
        }
    }

    #pictureOrbitLogic() {
        // Remove flat labels
        this.depthUpdate = true;
        this.intersections.forEach(mesh => {
            mesh.traverse(child => {
                if (child.name === 'edges'){
                    child.material.opacity = 0;
                } else if (child.name === 'label'){
                    child.removeFromParent();
                }
            });
        });

        // Restore overlay color
        const substrate = {value: 0.5};

        gsap.to(substrate,{
            value: 0.0,
            duration: 0.5,
            onUpdate: () => {
                this.material.uniforms.uSubstrateOpacity.value = substrate.value;
            }
        });

    }

    #pictureAutoZoom() {
        if (!this.zoomTarget) return;

        this.#box.setFromObject(this.zoomTarget).getSize(this.#zoomTargetSize);
        this.#box.setFromObject(this.zoomTarget).getCenter(this.#zoomTargetCenter);

        this.controls.target.set(this.#zoomTargetCenter.x,this.#zoomTargetCenter.y,0);
        this.camera.position.set(this.#zoomTargetCenter.x,this.#zoomTargetCenter.y,0);

        const maxSize = Math.max(this.#zoomTargetSize.x,this.#zoomTargetSize.y);

        this.camera.zoom = 1 / (maxSize / 2) - 0.05;
        this.camera.updateProjectionMatrix();
    }

    #pictureCylinderLogic() {

        // Create flat labels
        this.depthUpdate = false;
        this.intersections.forEach(mesh => {
            mesh.traverse(child => {
                if (child.name === 'edges'){
                    child.material.opacity = 1;
                    const label = flatLabelPictureLiteMode(mesh);
                    this.flatLabelsForPictureMode.push(label);
                }
            });
        });

        // Change overlay color
        const substrate = {value: 0};

        gsap.to(substrate,{
            value: 0.5,
            duration: 0.5,
            onUpdate: () => {
                this.material.uniforms.uSubstrateOpacity.value = substrate.value;
                this.#pictureAutoZoom();
            }
        });


    }

    switchSelections() {
        return;
        if (this.isActive() && this.modeIs('schematic')){
            const buildings = Buildings.getBuildingsArray();
            buildings.forEach(building => {
               if (!building.isActive) {
                    building.selectionsGroup.traverse(el => {
                       if (el.isMesh && el.visible && !el.name.startsWith(config.resources.bundle.selectionBoxes.floorPlugName)){
                           gsap.to(el.material,{
                               transparent: true,
                               opacity: 0,
                               onStart:() => {
                                 const edges = el.getObjectByName('Outline');
                                 if (edges) {
                                     edges.visible = false;
                                 }
                               },
                               onComplete: () => {
                                   el.visible = false;
                               }
                           });
                       }
                    });
               } else {
                   building.selectionsGroup.traverse(el => {
                       if (el.isMesh && !el.visible && !el.name.startsWith(config.resources.bundle.selectionBoxes.floorPlugName)){
                           gsap.to(el.material,{
                               transparent: true,
                               opacity: 1,
                               onStart: () => {
                                   el.visible = true;
                                   const edges = el.getObjectByName('Outline');
                                   if (edges) {
                                       edges.visible = true;
                                   }
                               },
                               onComplete: () => {
                                   el.material.transparent = false;
                               }
                           });
                       }
                   });
               }
            });
        }
    }

    render(renderer, writeBuffer, readBuffer) {
        if (this.isActive() && this.isVisible()){
            renderer.setRenderTarget(writeBuffer);
            renderer.render(this.overlay, this.camera);
            // this.material.uniforms.readBufferTexture.value = threeJsVariables.composer.composer.passes[0].over.texture;
            // this.material.uniforms.readBufferSize.value.set(readBuffer.width,readBuffer.height);
        }
    }

    zoomListener(target){
        if (!this.isVisible() || this.blockEvents) return;

        this.#decreaseBtn = document.querySelector('.zoom__controls__btn[data-zoom-btn="decrease"]');
        this.#increaseBtn = document.querySelector('.zoom__controls__btn[data-zoom-btn="increase"]');

        if (target){
            switch (target.dataset.zoomBtn) {
                case 'decrease':
                    !this.#decreaseBtn?.classList.contains('disabled') && this.controls.dollyOutCamera();
                    break;
                case 'increase':
                    !this.#increaseBtn?.classList.contains('disabled') && this.controls.dollyInCamera();
                    break;
            }
        }

        if (this.currentZoom === this.controls.minZoom) {
            this.#decreaseBtn?.classList.add('disabled');

            // if (this.#tryCount > 1){
            //     OrbitToMap();
            //     this.#decreaseBtn?.classList.remove('disabled');
            //     this.#tryCount = 0;
            //     return;
            // }

            showOrbitToMapButton();

            // this.#borderBounce();

        } else if (this.currentZoom > this.#bounceZoom && this.currentZoom < this.controls.maxZoom) {
            hideOrbitToMapButton();
            this.#increaseBtn?.classList.remove('disabled');
            //this.#decreaseBtn?.classList.remove('disabled');
            this.#tryCount = 0;
            this.blockEvents = false;
        } else if (this.currentZoom === this.controls.maxZoom){
            this.#increaseBtn?.classList.add('disabled');
            this.blockEvents = false;
        }
    }

    #borderBounce(){
        if (this.blockEvents || settingsData.activeViewMode !== ViewMode.Orbit ) return;
        this.blockEvents = true;

        const bounce = { value: this.currentZoom};

        gsap.to(bounce,{
            value: this.currentZoom * 1.1,
            duration: 0.2,
            onStart: () => {
                this.#bounceZoom = this.currentZoom * 1.1;
            },
            onUpdate: () => {
                this.currentZoom = bounce.value;
                this.camera.zoom = this.currentZoom;
                this.camera.updateProjectionMatrix();
            },
            onComplete: () => {
                this.blockEvents = false;
                this.#decreaseBtn?.classList.remove('disabled');
                this.#tryCount ++;
            }
        });
    }

    runProjectStartAdditionalFunctions(){
        if (!this.isActive() || BmbyMode.active) return;

        compassVisibleToggle('hide');
        createOrbitToMapButton();

        threeJsVariables.orbitControls.enabled = false;
        threeJsVariables.trackballControls.enabled = false;

    }

    updateAspect () {
        if (!this.isActive() || this.debugActive) return;
        threeJsVariables.renderer.getSize(this.#renderSizeVec);
        const imageAspect = this.imageWidth / this.imageHeight;
        const renderAspect = this.#renderSizeVec.x / this.#renderSizeVec.y;

        if (renderAspect < imageAspect) {
            this.aspect.set(renderAspect / imageAspect, 1.0);
        } else {
            this.aspect.set(1.0, imageAspect / renderAspect);
        }
    }

    preventPointerEvents(action = undefined){
        if (!this.isActive()) return false;

        if (action !== undefined){
            this.blockPointerEvents = action;
        } else {
            return this.blockPointerEvents;
        }
    }

    controlsToggle(enable){
        if (!this.isActive()) return;
        threeJsVariables.orbitControls.enabled = enable;
        threeJsVariables.trackballControls.enabled = enable;
    }

    resizeContainer () {
        if (!this.isActive() || !this.isVisible()) return;
        this.updateAspect();
        this.overlay.scale.set(1 / this.aspect.x, 1 / this.aspect.y, 1);
        this.rotor?.resize();
        // if (isMobile || isTablet) {
        //     this.#pictureAutoZoom();
        // }
        this.#controlsHandler(undefined,true);
        this.gimbal?.recalibrate();
    }

    setDefaultPosition(){
        if (!this.isActive() || !this.isVisible()) return;

        this.updateAspect();
        this.overlay.scale.set(1 / this.aspect.x, 1 / this.aspect.y, 1);

        this.currentZoom = this.settings.zoom.initial;
        this.camera.zoom = this.currentZoom;
        this.controls.target.set(this.settings.zoom.position.x,this.settings.zoom.position.y,0);
        this.camera.position.set(this.settings.zoom.position.x,this.settings.zoom.position.y,0);
        this.camera.updateProjectionMatrix();

        const planeSizeX = 2 * this.overlay.scale.x;
        const planeSizeY = 2 * this.overlay.scale.y;
        const x = (planeSizeX * this.currentZoom - 2) / 2 / this.currentZoom;
        const y = (planeSizeY * this.currentZoom - 2) / 2 / this.currentZoom;

        const vMin = new Vector3(-x ,-y ,0);
        const vMax = new Vector3(x,y ,0);

        this.controls.target.clamp(vMin,vMax);
        this.controls.object.position.clamp(vMin,vMax);

        this.#maxPan.set(x,y,0);
        this.#minPan.set(-x,-y ,0);

        this.intersections.forEach(mesh => {
            mesh.traverse(child => {
                if (child.name.startsWith('labelTower')){
                    child.element.classList.add('active');
                }
            });
        });

    }

    updateTime (delta) {
        if (!this.isActive() || !this.isVisible()) return;
        this.controls.update();
        this.material.uniforms.uTime.value += delta;
        this.sea.updateTime(delta);
        this.cars.updateTime(delta);
        this.sky.updateTime(delta);
        this.windows.updateTime(delta);
        this.trees.updateTime(delta);
    }

    updateTimesOfDay(time) {
        if (!this.isActive() || !this.isVisible()) return;

        this.material.uniforms.uTimesOfDay.value = time;
    }

    showLiteMode () {
        if (this.blockEvents) return;

        if (this.modeIs('picture') || this.modeIs('empty')){
            this.resizeContainer();
            this.cars.switcher('show');
            this.sea.switcher('show');
            this.plug.switcher('show');
            this.sky.switcher('show');
            this.trees.switcher('show');
            this.windows.switcher('show');
            this.neighborhood.switcher('show');

            return;
        }

        this.blockEvents = true;

        const scale = { value: this.material.uniforms.uCircleScale.value};

        gsap.to(scale,{
            value: 0,
            duration: 1.0,
            onUpdate: () => {
                this.material.uniforms.uCircleScale.value = scale.value;
                this.resizeContainer();
            },
            onStart: () => {
                this.#layerVisibility(true);
                this.rotor?.enable(true);
                this.enabled = true;
                this.updateAspect();
                this.preventPointerEvents(true);
                threeJsVariables.orbitControls.enabled = false;
                threeJsVariables.trackballControls.enabled = false;
            },
            onComplete: () => {
                settingsData.objects_to_intersection = settingsData.objects_to_intersection.concat(this.intersections);
                this.intersections.forEach(mesh => {
                    mesh.traverse(child => {
                        if (child.name.startsWith('labelTower')){
                            child.element.classList.add('active');
                        }
                    });
                });
                this.amenitiesToggle('show');
                this.buildingLabelsToggle('show');
                this.blockEvents = false;
                this.controls.enabled = true;
                this.preventPointerEvents(false);
                this.resizeContainer();
                this.cars.switcher('show');
                this.sea.switcher('show');
                this.plug.switcher('show');
                this.sky.switcher('show');
                this.trees.switcher('show');
                this.windows.switcher('show');
                this.neighborhood.switcher('show');
                this.sceneVisibility(false);
            }
        })
    }

    #layerVisibility(state){
        this.overlay.visible = state;
    }

    hideLiteMode () {
        if (this.blockEvents) return;

        if (this.modeIs('picture') || this.modeIs('empty')) {
            this.cars.switcher('hide');
            this.sea.switcher('hide');
            this.plug.switcher('hide');
            this.sky.switcher('hide');
            this.trees.switcher('hide');
            this.windows.switcher('hide');
            this.neighborhood.switcher('hide');
            return;
        }

        this.blockEvents = true;
        this.controls.enabled = false;
        this.preventPointerEvents(true);
        this.amenitiesToggle('hide');
        this.buildingLabelsToggle('hide');
        this.sceneVisibility(true);

        const scale = { value: 0};
        const duration = BmbyMode.active ? 0.00001 : 2;

        gsap.to(scale,{
            value: 1.5,
            duration: duration,
            onStart: () => {
                this.cars.switcher('hide');
                this.sea.switcher('hide');
                this.plug.switcher('hide');
                this.sky.switcher('hide');
                this.trees.switcher('hide');
                this.windows.switcher('hide');
                this.neighborhood.switcher('hide');
                this.rotor?.enable(false);
            },
            onUpdate: () => {
                this.material.uniforms.uCircleScale.value = scale.value;
                if (scale.value > 1.15) {
                    this.enabled = false;
                }
                this.resizeContainer();
            },
            onComplete: () => {
                // this.enabled = false;
                this.#layerVisibility(false);
                this.intersections.forEach(mesh => {
                    mesh.traverse(child => {
                        if (child.name.startsWith('labelTower')){
                            child.element.classList.remove('active');
                        }
                    });
                });
                this.preventPointerEvents(false);
                threeJsVariables.orbitControls.enabled = true;
                threeJsVariables.trackballControls.enabled = true;
                this.blockEvents = false;
            }
        })
    }
    
   sceneVisibility(state) {
        if (!this.isActive())
            return;
        
        ['Project Buildings','Scene objects','Background'].forEach(name => {
            const object = threeJsVariables.scene.getObjectByName(name);
            if (object) {
                object.visible = state;
            }
        });
   }

   cloudsToggle(action) {
       this.sky.cloudsToggle(action);
   }

   rainbowToggle(action) {
        this.sky.rainbowToggle(action);
   }

    mapAnimation(state){
        if (this.blockEvents)
            return;

        this.blockEvents = true;
        this.controls.enabled = false;
        this.preventPointerEvents(true);
        this.amenitiesToggle('hide');
        this.buildingLabelsToggle('hide');

        const progress = { start: 0, end: 0 };

        this.intersections.forEach(mesh => {
            mesh.traverse(child => {
                if (child.name.startsWith('labelTower')){
                    child.element.classList.remove('active');
                }
            });
        });

        switch (state) {
            case 'toMap':
                progress.end = 1.3;
                this.controls.enabled = false;
                break;
            case 'toOrbit':
                progress.start = 1.3;
                progress.end = 0.0;
                this.controls.enabled = true;
                break;

        }

        return new Promise(resolve => {
            gsap.to(progress,{
                start: progress.end,
                duration: 2.0,
                onUpdate: () => {
                    this.material.uniforms.orbitToMapProgress.value = progress.start;
                },
                onComplete: () => {
                    this.material.uniforms.orbitToMapProgress.value = 0;
                    this.currentZoom = this.settings.zoom.initial;
                    this.camera.zoom = this.currentZoom;
                    this.blockEvents = false;
                    this.controls.enabled = true;
                    this.preventPointerEvents(false);
                    if (state === 'toOrbit'){
                        this.intersections.forEach(mesh => {
                            mesh.traverse(child => {
                                if (child.name.startsWith('labelTower')){
                                    child.element.classList.add('active');
                                }
                            });
                        });
                        this.amenitiesToggle('show');
                        this.buildingLabelsToggle('show');
                    }

                    this.#controlsHandler(undefined,true);
                    this.sea.switcher('show');
                    this.plug.switcher('show');
                    this.sky.switcher('show');
                    this.trees.switcher('show');
                    this.cars.switcher('show');
                    this.windows.switcher('show');
                    this.neighborhood.switcher('show');
                    resolve();
                },
                onStart: () => {
                    hideOrbitToMapButton();
                    this.#decreaseBtn?.classList.remove('disabled');
                    this.#increaseBtn?.classList.remove('disabled');
                    this.sea.switcher('hide');
                    this.plug.switcher('hide');
                    this.cars.switcher('hide');
                    this.sky.switcher('hide');
                    this.trees.switcher('hide');
                    this.windows.switcher('hide');
                    this.neighborhood.switcher('hide');
                }
            });
        });
    }

    moveCameraToBuilding(name) {
    //     console.log('move camera to building');
    //     return;
    //     const start = {
    //         zoom: this.currentZoom,
    //         x: this.material.uniforms.uMouse.value.x,
    //         y: this.material.uniforms.uMouse.value.y
    //     };
    //
    //     const buildingObject = this.maps.find(el => el.building === name);
    //     const buildingCoords = buildingObject.coords[0];
    //
    //     gsap.to(start,{
    //         zoom: this.settings.maxZoom,
    //         x: buildingCoords[0],
    //         y: buildingCoords[1],
    //         duration: 1,
    //         onUpdate: () => {
    //             this.currentZoom = start.zoom;
    //             this.material.uniforms.zoom.value = this.currentZoom;
    //
    //             this.#dragMousePosition.set(start.x,start.y);
    //
    //             this.#dragMousePosition.clamp(
    //                 this.#dragV1.set(-((this.currentZoom - 1) / this.currentZoom) ,-((this.currentZoom - 1) / this.currentZoom)),
    //                 this.#dragV2.set((this.currentZoom - 1) / this.currentZoom, (this.currentZoom - 1) / this.currentZoom)
    //             );
    //
    //             this.material.uniforms.uMouse.value.copy(this.#dragMousePosition);
    //         },
    //         onStart: () => {
    //             this.blockHover = true;
    //             this.material.uniforms.hoverTexture.value = null;
    //         },
    //         onComplete: () => {
    //             this.#resizeHoverPoints();
    //             this.material.uniforms.hoverTexture.value = this.hoverTextures[buildingObject.building];
    //         }
    //     });
    //
    }
    //
    moveCameraFromBuilding() {
    //     console.log('move camera from building');
    //     return;
    //     const start = {
    //         zoom: this.currentZoom,
    //         x: this.material.uniforms.uMouse.value.x,
    //         y: this.material.uniforms.uMouse.value.y
    //     };
    //
    //     gsap.to(start, {
    //         zoom: this.settings.initialZoom,
    //         x: 0,
    //         y: 0,
    //         duration: 0.5,
    //         onUpdate: () => {
    //             this.currentZoom = start.zoom;
    //             this.material.uniforms.zoom.value = this.currentZoom;
    //
    //             this.#dragMousePosition.set(start.x,start.y);
    //             //
    //             this.#dragMousePosition.clamp(
    //                 this.#dragV1.set(-((this.currentZoom - 1) / this.currentZoom) ,-((this.currentZoom - 1) / this.currentZoom)),
    //                 this.#dragV2.set((this.currentZoom - 1) / this.currentZoom, (this.currentZoom - 1) / this.currentZoom)
    //             );
    //
    //             this.material.uniforms.uMouse.value.copy(this.#dragMousePosition);
    //         },
    //         onComplete: () => {
    //             this.#resizeHoverPoints();
    //             this.material.uniforms.hoverTexture.value = null;
    //             this.blockHover = false;
    //         }
    //     })
    }

    isVisible(){
        return this.overlay?.visible;
    }

    isActive () {
        return this.active;
    }

    modeIs (name) {
        if (!this.active) return false;

        return this.mode === name;
    }

}

class LiteModeAmenities {

    constructor (data) {
        if (!data.url.length) return;
        this.data = data;
        this.url = new URL(data.url);
        this.type = this.url.pathname.match(/\.(htm|html)/g) ? '360' : 'img';
        this.preview360Url = data?.preview360;
        this.gallery = data?.gallery && data?.gallery.length > 0 ? data.gallery : null;
        this.label = this.createLabel();
        return this.label;
    }

    createLabel() {
        const container = new BaseElement()
            .addClass('orbit-360-label');
        const titles = this.data.title;
        if (titles && Object.keys(titles).length) {
            const title = new BaseElement('span')
                .addClass('poi__title','orbit-360__title','language-string')
                .innerHtml(titles[getCurrentLang()])
                .appendTo(container.DOMElement);
            Object.entries(titles).forEach(([lang,text]) => {
                title.addAttribute(`data-lang-${lang}`,text);
            });
        }
        const icon = new BaseElement()
            .appendTo(container.DOMElement);

        if (this.type === '360') {
            if (Boolean(this.preview360Url)) {
                this.#addPreviewImageToLabel(container, this.preview360Url);
            } else {
                icon.addClass('icon-360');
            }
        } else {
            this.#addPreviewImageToLabel(container, this.url);
        }

        const label = new CSS2DObject(container.DOMElement);
        label.userData.zoom = 1;
        label.element.addEventListener('click',this.clickHandler.bind(this));
        label.name =`layer-poi__orbit-360`;
        return label;
    }

    #addPreviewImageToLabel(container, imageUrl) {
        container.addClass('image-icon');
        const labelDOM = container.DOMElement;
        const previewIconImage = Cloudflare.resize(imageUrl,{
            width: 42,
            quality: 60
        });
        labelDOM.style.backgroundImage = `url(${previewIconImage})`;
    }

    clickHandler() {
        settingsData.stopRender = true;
        this.mainContainer = new BaseElement()
            .addClass('orbit-360-viewer')
            .appendTo(document.body);
        const content = new BaseElement()
            .addClass('orbit-360-viewer__content')
            .appendTo(this.mainContainer.DOMElement);

        let viewer;

        if (this.type === '360') {
            new Button()
                .addClass('icon-close')
                .event('click', this.closeHandler.bind(this))
                .appendTo(content.DOMElement);
            this.preloader = new BaseElement()
                .addClass('orbit-360-preloader')
                .appendTo(content.DOMElement);
            new DreamsPreloader(this.preloader.DOMElement,'orbit-360-pr').show();
            viewer = new BaseElement('iframe')
                .addClass('orbit-360-viewer__player','player-iframe')
                .addAttribute('src',this.url.href)
                .appendTo(content.DOMElement);
            viewer.DOMElement.onload = () => this.preloader.remove();
        } else {
            viewer = new BaseElement('div')
                .addClass('orbit-360-viewer__player','player-img')
                .appendTo(content.DOMElement);

            const data = this.gallery && this.gallery.length ? this.gallery : [this.url.href];
            const gallery = new PhotoSwiperGallery(data,0,viewer.DOMElement);
            gallery.onClose = () => this.closeHandler();
            gallery.open();
        }
    }

    closeHandler() {
        this.mainContainer.remove();
        settingsData.stopRender = false;
    }
}

class SensorData {
    constructor() {
        this.alpha = 0;
        this.beta = 0;
        this.gamma = 0;
        this.orientation = window.orientation ? window.orientation : 0;
    }
}

// Transforms accelerometer alpha, beta, gamma information,
// then returns readable angles via yaw, pitch & roll
// yaw: y-axis rotations
// pitch: x-axis rotations
// roll: z-axis rotations

class Gimbal {
    constructor() {
        this.quaternion = new Quaternion();
        this.quatOrigin = new Quaternion();
        this.axisUp = new Vector3(0, 1, 0);
        this.axisFwd = new Vector3(0, 0, 1);
        this.vectorUp = new Vector3();
        this.vectorFwd = new Vector3();
        this.sensorRotations = new Object3D();
        this.data = new SensorData();
        this.requestRecal = false;

        this.RAD = Math.PI / 180;
        this.DEG = 180 / Math.PI;

        this.eulerOrigin = new Euler();

        if (typeof window.orientation !== "undefined") {
            this.eulerOrigin.set(
                90 * this.RAD,
                180 * this.RAD,
                (180 + window.orientation) * this.RAD
            );
        }
    }

    enable() {
        this.onDeviceReorientation();

        window.addEventListener("deviceorientation", this.onSensorMove.bind(this), false);
        window.addEventListener("orientationchange", this.onDeviceReorientation.bind(this), false);
    }

    disable() {
        window.removeEventListener("deviceorientation", this.onSensorMove.bind(this), false);
        window.removeEventListener("orientationchange", this.onDeviceReorientation.bind(this), false);
    }

    // (experimental)
    recalibrateYPR() {
        this.axisUp.set(0, 1, 0);
        this.axisFwd.set(0, 0, 1);
        this.axisUp.applyQuaternion(this.sensorRotations.quaternion);
        this.axisFwd.applyQuaternion(this.sensorRotations.quaternion);
    }

    // Recalibrates gimbal axes to match current phone orientation
    recalibrate() {
        this.sensorRotations.setRotationFromEuler(this.eulerOrigin);

        // Apply gyroscope rotations
        this.sensorRotations.rotateZ(this.data.alpha * this.RAD);
        this.sensorRotations.rotateX(this.data.beta * this.RAD);
        this.sensorRotations.rotateY(this.data.gamma * this.RAD);
        this.sensorRotations.rotation.z -= this.data.orientation;

        this.quatOrigin.copy(this.sensorRotations.quaternion);
        this.quatOrigin.invert();
    }

    //////////////////////////////////////////// EVENT LISTENERS  /////////////////////////////////////////////
    // When switching from portrait <---> landscape
    onDeviceReorientation() {
        this.data.orientation = (window.orientation * this.RAD) || 0;
        // this.recalibrateYPR();
    }

    // Update data when device detects movement
    onSensorMove(event) {
        // Alpha = z axis [0 ,360]
        // Beta = x axis [-180 , 180]
        // Gamma = y axis [-90 , 90]
        this.data.alpha = event.alpha;
        this.data.beta = event.beta;
        this.data.gamma = event.gamma;
    }

    // Called once per frame
    update() {
        // Reset rotation
        this.sensorRotations.setRotationFromEuler(this.eulerOrigin);
        this.sensorRotations.applyQuaternion(this.quatOrigin);

        // Apply gyroscope rotations
        this.sensorRotations.rotateZ(this.data.alpha * this.RAD);
        this.sensorRotations.rotateX(this.data.beta * this.RAD);
        this.sensorRotations.rotateY(this.data.gamma * this.RAD);
        this.sensorRotations.rotation.z -= this.data.orientation;

        // Extract quaternion from object
        this.quaternion.copy(this.sensorRotations.quaternion);
        this.quaternion.invert();

        // Apply quat to axes
        this.vectorUp.copy(this.axisUp);
        this.vectorUp.applyQuaternion(this.quaternion);
        this.vectorFwd.copy(this.axisFwd);
        this.vectorFwd.applyQuaternion(this.quaternion);

        // Yaw is heading east (-) or west (+) rotation around y-axis.
        this.yaw = Math.atan2(this.vectorFwd.x, this.vectorFwd.z);

        // Pitch is pointing above or below (+/-) horizon line
        this.pitch = Math.atan2(this.vectorUp.z, this.vectorUp.y);

        // Roll is left (-) or right (+) rotation around local z-axis
        this.roll = Math.atan2(-this.vectorUp.x, this.vectorUp.y);
    }
}
